import React, { useContext, useEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Box,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useLogoutUserMutation } from "redux/api/authApi";
import { showErrorToast } from "./Toast/Toast";
import logoHeader from "assets/logoHeader.svg";
import { useDispatch } from "react-redux";
import { resetStep } from "redux/features/stepperSlice";
import { clearCurrentOperation } from "redux/features/operationSlice";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../LanguageProvider";
import { useIntl } from "react-intl";
import { isIOS } from "react-device-detect";
import { useAppSelector } from "redux/store";
import { StatusBar } from "./Shared/StatusBar";

const MenuDrawer: React.FC = () => {
  const context = useContext(LanguageContext);
  const { formatMessage,locale } = useIntl();
  const isStandalone = useAppSelector(
    (state) => state.configState.isStandalone
  );

  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();


  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    setIsOpen(open);
  };
  const handleReset = () => {
    dispatch(clearCurrentOperation()); // Reinicia operación
    dispatch(resetStep()); // Reinicia el stepper
    setIsOpen(false);
    navigate("/");
  };

  const [logoutUser, { isLoading, isSuccess, error, isError }] =
    useLogoutUserMutation();
  if (!context) {
    throw new Error("LanguageSwitcher must be used within a LanguageProvider");
  }

  const { changeLanguage } = context;


  useEffect(() => {
    if (isSuccess) {
      navigate("/login");
    }

    if (isError) {
      if (Array.isArray((error as any).data.error)) {
        (error as any).data.error.forEach((el: any) =>
          showErrorToast(el.message)
        );
      } else {
        showErrorToast((error as any).data.message);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const onLogoutHandler = async () => {
    logoutUser();
  };

  const handleClickStart = () => {
    navigate("/"); // Esto llevará a la ruta "/"
    setIsOpen(false);
  };

  const handleClickHistory = () => {
    navigate("/history"); // Esto llevará a la ruta "/history"
    setIsOpen(false);
  };

  const handleClickPendant = () => {
    navigate("/pendant"); // Esto llevará a la ruta "/pendant"
    setIsOpen(false);
  };

  return (
    <>
      {/* Botón para abrir el menú */}
      <IconButton onClick={toggleDrawer(true)} sx={{ color: "#FFFFFF" }} edge="start">
        <MenuIcon />
      </IconButton>

      {/* Menú desplegable (Drawer) */}
      <Drawer anchor="right" open={isOpen} onClose={toggleDrawer(false)} sx={{ zIndex: 1300 }} >
        <Box
          sx={{
            width: 300,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between", // Asegura que el botón cierre sesión esté abajo
            bgcolor: "black",
            color: "white",
          }}
        >
            {isStandalone && isIOS && <StatusBar/>}

          {/* Cabecera del menú */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0.5rem 1rem",
              borderBottom: "1px solid white",
            }}
          >
            {/* Botón de cerrar (X) */}
            <IconButton
              onClick={toggleDrawer(false)}
              sx={{
                color: "white",
                fontSize: "32px",
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>

            {/* Logo */}
            <img
              height={80}
              width={80}
              alt="Logo"
              src={logoHeader}
              style={{ objectFit: "contain" }}
            />

            {/* Botones adicionales */}
            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              <Button
                sx={{
                  color: "white",
                  border: "1px solid white",
                  fontSize: "12px",
                  padding: "0.25rem 0.5rem",
                  minWidth: "auto",
                  bgcolor: locale === "es" ? "gray" : "transparent", // Resaltar si es el idioma actual
                  "&:hover": { bgcolor: "gray" },
                }}
                onClick={() => changeLanguage("es")}
              >
                ES
              </Button>
              <Button
                sx={{
                  color: "white",
                  border: "1px solid white",
                  fontSize: "12px",
                  padding: "0.25rem 0.5rem",
                  minWidth: "auto",
                  bgcolor: locale === "en" ? "gray" : "transparent", // Resaltar si es el idioma actual
                  "&:hover": { bgcolor: "gray" },
                }}
                onClick={() => changeLanguage("en")}
              >
                EN
              </Button>
            </Box>
          </Box>

          {/* Lista de enlaces */}
          <List
            sx={{
              backgroundColor: "black", // Fondo negro
              paddingTop: "10px", // Padding arriba
              paddingBottom: "10px", // Padding abajo
              width: "100%", // Para asegurarse de que ocupe todo el ancho
              display: "flex", // Para alinear el contenido
              flexDirection: "column", // Asegura que la lista esté en columna
              alignItems: "center", // Centra horizontalmente los elementos
            }}
          >
            <ListItem onClick={handleClickStart} sx={{ width: "100%" }}>
              <ListItemText primary={formatMessage({ id: "drawer.home" })} sx={{ textAlign: "center", color: "white" }} />
            </ListItem>
            <Divider sx={{ width: "80%", margin: "10px 0", backgroundColor: "#ffffff" }} />
            <ListItem onClick={handleClickHistory} sx={{ width: "100%" }}>
              <ListItemText primary={formatMessage({ id: "drawer.history" })} sx={{ textAlign: "center", color: "white" }} />
            </ListItem>
            <Divider sx={{ width: "80%", margin: "10px 0", backgroundColor: "#ffffff" }} />
            <ListItem onClick={handleClickPendant} sx={{ width: "100%" }}>
              <ListItemText primary={formatMessage({ id: "drawer.pendant" })} sx={{ textAlign: "center", color: "white" }} />
            </ListItem>
            <Divider sx={{ width: "80%", margin: "10px 0", backgroundColor: "#ffffff" }} />
            <ListItem onClick={handleReset} sx={{ width: "100%" }}>
              <ListItemText primary={formatMessage({ id: "drawer.new" })} sx={{ textAlign: "center", color: "white" }} />
            </ListItem>
          </List>

          {/* Botón de cerrar sesión */}
          <Button
            onClick={onLogoutHandler}
            sx={{
              margin: 2,
              bgcolor: "black",
              color: "white",
              border: "1px solid white",
              marginTop: "auto", // Empuja el botón hacia el final del contenedor
              "&:hover": { bgcolor: "gray" },
            }}
          >
            {formatMessage({ id: "logout" })}
          </Button>
        </Box>
      </Drawer>

    </>
  );
};

export default MenuDrawer;
